import { useMemo, useRef, useState } from 'react';
import { brightpathTheme } from '@youscience/theme';
import { ThemeProvider } from '@mui/material';
import {
  FormNames,
  CounselorRecommendationSchema,
  CounselorRecommendationsDocument,
  YesNo,
} from '@youscience/commonapp-service-common';

import { FormState } from '@constants/commonApp/types';

import { useApplicantForm } from '@pages/StudentDetailsPage/hooks/useApplicantForm';

import useRecommenderWorkspaceStore from '@store/RecommenderWorkspaceStore';
import useRecommenderStore from '@store/RecommenderStore';
import useNotificationStore from '@store/NotificationStore';

import { useUserProfile } from '@hooks/useUserProfile';

import FormHeader from '../../common/FormHeader';
import FormFooter from '../../common/FormFooter';

import {
  checkCounselorProfileValidity,
  checkFormValidity,
  PREVIEW_ERROR_NOTIFICATION,
  REVIEW_NOT_READY_ERROR_NOTIFICATION,
  onSaveErrorNotification,
  onSaveSuccessNotification,
  onSubmitSuccessNotification,
  onUnsubmitSuccessNotification,
} from '../../common/formUtils';
import ReviewPdf, { ReviewTypes } from '../../common/ReviewPdf';
import Submissions from '../../common/Submissions';
import { FormAvailabilityStatus, FormCompletionStatus } from '../../common/enums';

import { formatDateWithFallback } from '@utils/formatter';
import { TabSections } from '@interfaces/recommenderWorkspace';
import WrittenEvaluation from './components/WrittenEvaluation';
import { WrittenEvaluationFields } from './components/WrittenEvaluation/WrittenEvaluation';
import { useRemoveShowErrorOnUnmount, removeShowErrorsFromUrl } from '@utils/navigation';

const formName = FormNames.RECOMMENDATION;

export const CounselorRecommendation = () => {
  useRemoveShowErrorOnUnmount();

  const { notify } = useNotificationStore((state) => state);
  const { recommenderProfile } = useRecommenderStore((store) => store);
  const { userId } = useUserProfile();
  const recommendation = useApplicantForm<CounselorRecommendationsDocument>(formName);
  const initialFormState = useMemo(
    () => ({
      [TabSections.WrittenEvaluation]: recommendation.content,
    }),
    [recommendation.content],
  );
  const form = useRef(initialFormState);
  const [formErrors, setFormErrors] = useState({});
  const [isExtended, setIsExtended] = useState(true);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [reviewType, setReviewType] = useState<ReviewTypes>(ReviewTypes.PREVIEW_FORM);

  const isSubmitDisabled = recommendation.metadata.availabilityStatus !== FormAvailabilityStatus.AVAILABLE;
  const isSubmitted =
    recommendation.metadata.completionStatus === FormCompletionStatus.SUBMITTED ||
    recommendation.metadata.completionStatus === FormCompletionStatus.DOWNLOADED;
  const { applicantId, recommenderId } = recommendation.metadata;

  const onFormChangeHandler =
    <K,>(section: TabSections) =>
    (sectionFormState: FormState<K>) => {
      form.current = { ...form.current, [section]: sectionFormState.data };
    };

  const validateForm = () => {
    const formValid = checkFormValidity(form.current?.writtenEvaluation, CounselorRecommendationSchema);

    const writtenEval = form.current?.writtenEvaluation;
    const hasEvaluation = !!writtenEval?.evaluation || !!writtenEval?.evaluation2;
    const hasNoEvaluationSet = writtenEval?.noEvaluation === YesNo.Yes;

    // Form is only valid if either:
    // 1. noEvaluation is "0" (indicating they won't provide evaluation) OR
    // 2. they have provided either an evaluation file or written evaluation2
    const customValidation = hasNoEvaluationSet || hasEvaluation;

    const profileValid = checkCounselorProfileValidity(recommenderProfile);

    return {
      form: !formValid || !customValidation,
      recommenderProfile: !profileValid.personal,
      schoolProfile: !profileValid.school,
    };
  };

  const handleSaveForLater = async (isHideNotification?: boolean) => {
    await recommendation.save({ ...form.current?.writtenEvaluation, applicantId, recommenderId });
    if (isHideNotification) return;

    recommendation.isError ? onSaveErrorNotification() : onSaveSuccessNotification(formName);
  };

  const previewForm = async () => {
    if (isSubmitted) {
      setIsPreviewOpen(true);
      await recommendation.getSubmitted({ applicantId, recommenderId });
    } else {
      await handleSaveForLater(true);

      const formErrors = validateForm();

      setFormErrors(formErrors);
      setIsPreviewOpen(true);
      const hasFormErrors = Object.values(formErrors).some(Boolean);

      if (!hasFormErrors) {
        removeShowErrorsFromUrl();
        await recommendation.preview({ ...form.current?.writtenEvaluation, applicantId, recommenderId });
      }
    }

    useRecommenderWorkspaceStore.subscribe((state) => {
      if (state.isError) {
        notify(isSubmitted ? REVIEW_NOT_READY_ERROR_NOTIFICATION : PREVIEW_ERROR_NOTIFICATION);
        setIsPreviewOpen(false);
      }
    });
  };

  const submitForm = async (signature: string, signedDate: string) => {
    await recommendation.submit({
      applicantId,
      recommenderId,
      counselorApproval: { isReviewed: true, isAffirmed: true, signature, signedDate, userId: userId as string },
    });

    recommendation.isError ? onSaveErrorNotification() : onSubmitSuccessNotification(formName);
  };

  const handleOnSubmit = () => {
    setReviewType(ReviewTypes.REVIEW_AND_SUBMIT);
    void previewForm();
  };

  const handleOnPreview = () => {
    setReviewType(isSubmitted ? ReviewTypes.REVIEW_FORM : ReviewTypes.PREVIEW_FORM);
    void previewForm();
  };

  const handleOnUnsubmit = async () => {
    await recommendation.unsubmit({ applicantId, recommenderId });

    recommendation.isError ? onSaveErrorNotification() : onUnsubmitSuccessNotification(formName);
  };

  if (!recommendation.isReady) {
    return null;
  }

  return (
    <ThemeProvider theme={brightpathTheme}>
      <FormHeader
        name="Counselor recommendation"
        tooltipTitle="The counselor recommendation lets you share unique insights about your student. You may submit this form at any time."
        deadline={formatDateWithFallback(recommendation.metadata?.deadline)}
        status={recommendation.metadata?.completionStatus || FormCompletionStatus.NEW}
        onPreviewClick={handleOnPreview}
        onSaveForLaterClick={handleSaveForLater}
        onSubmitClick={handleOnSubmit}
        onUnsubmitClick={handleOnUnsubmit}
        areAllActionsDisabled={recommendation.isSavingForm}
        isSubmitDisabled={isSubmitDisabled}
        isFormSumbitted={isSubmitted}
        formName={formName}
      />

      {isSubmitted ? (
        <Submissions formName={formName} onSubmit={handleOnSubmit} />
      ) : (
        <>
          <WrittenEvaluation
            writtenEvaluation={recommendation.content}
            isExtended={isExtended}
            onExpand={() => setIsExtended(!isExtended)}
            onFormChange={onFormChangeHandler<WrittenEvaluationFields>(TabSections.WrittenEvaluation)}
            isSaved={Object.keys(recommendation.sectionIsSaved).length > 0}
            applicantName={recommendation.metadata.studentName}
            showError={
              Object.values(formErrors).some(Boolean) &&
              !form.current?.writtenEvaluation?.evaluation &&
              !form.current?.writtenEvaluation?.evaluation2 &&
              form.current?.writtenEvaluation?.noEvaluation !== YesNo.Yes
            }
          />
        </>
      )}

      <FormFooter
        onPreviewClick={handleOnPreview}
        onSaveForLaterClick={handleSaveForLater}
        onSubmitClick={handleOnSubmit}
        onUnsubmitClick={handleOnUnsubmit}
        isSubmitDisabled={isSubmitDisabled}
        isFormSumbitted={isSubmitted}
        formName={formName}
      />

      {isPreviewOpen ? (
        <ReviewPdf
          title="Counselor Recommendation"
          subtitle="Please review the PDF preview of your form. Scroll to the bottom if necessary to confirm and continue."
          isOpen={isPreviewOpen}
          isLoading={recommendation.isLoadingPreview}
          reviewType={reviewType}
          pdf={recommendation.previewPdfURL}
          onClose={() => setIsPreviewOpen(false)}
          onContinue={(signature?: string, date?: string) => {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            if (reviewType === ReviewTypes.REVIEW_AND_SUBMIT) submitForm(signature!, date!);
          }}
          errors={formErrors}
        />
      ) : null}
    </ThemeProvider>
  );
};
